/**
 * Phase 45 (2026-07-28) - Budget Settings modal.
 *
 * Small dialog for setting a monthly expenses budget. Opened either from
 * `/settings/budgets` (as a page) or from the Total Expenses hero card's
 * gear/CTA button (as a modal).
 */
import { useEffect, useState } from "react";
import { X, DollarSign } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

function currentMonthYYYYMM() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

export default function BudgetSettingsModal({ isOpen, onClose, onSaved }) {
  const [month, setMonth] = useState(currentMonthYYYYMM());
  const [amount, setAmount] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/user/budget?kind=monthly_expenses&month=${month}`,
        );
        if (cancelled) return;
        setIsDefault(!!data.is_default);
        setAmount(
          data.amount_usd !== null && data.amount_usd !== undefined
            ? String(data.amount_usd)
            : "",
        );
      } catch (e) {
        // fallthrough - render empty
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, month]);

  const save = async () => {
    const numeric = Number(amount);
    if (!Number.isFinite(numeric) || numeric < 0) {
      toast.error("Enter a valid budget amount");
      return;
    }
    setSaving(true);
    try {
      await api.put("/user/budget", {
        kind: "monthly_expenses",
        month,
        amount_usd: numeric,
      });
      toast.success(`Budget saved for ${month}`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error("Failed to save budget");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
      data-testid="budget-settings-modal"
    >
      <div
        className="w-full max-w-md rounded-2xl p-6"
        style={{
          backgroundColor: "var(--bg-surface)",
          color: "var(--text-primary)",
          boxShadow: "var(--shadow-popover)",
          border: "1px solid var(--border-default)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">Budget Settings</h3>
            <p className="mt-0.5 text-xs" style={{ color: "var(--text-muted)" }}>
              Set your monthly expense target so the dashboard ring reflects your
              actual usage.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 hover:opacity-70"
            style={{ color: "var(--text-secondary)" }}
            aria-label="Close"
            data-testid="budget-modal-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium mb-1"
                   style={{ color: "var(--text-secondary)" }}>
              Effective month
            </label>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-full rounded-md border px-3 py-1.5 text-sm bg-transparent"
              style={{
                borderColor: "var(--border-default)",
                color: "var(--text-primary)",
              }}
              data-testid="budget-month-input"
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1"
                   style={{ color: "var(--text-secondary)" }}>
              Monthly expense budget (USD)
            </label>
            <div
              className="flex items-center rounded-md border px-3 py-1.5 gap-2"
              style={{ borderColor: "var(--border-default)" }}
            >
              <DollarSign className="h-4 w-4" style={{ color: "var(--text-muted)" }} />
              <input
                type="number"
                step="100"
                min="0"
                value={amount}
                placeholder={loading ? "Loading..." : "e.g. 30000"}
                onChange={(e) => setAmount(e.target.value)}
                className="flex-1 bg-transparent text-sm outline-none tabular-nums"
                style={{ color: "var(--text-primary)" }}
                data-testid="budget-amount-input"
              />
            </div>
            {isDefault && amount && !loading && (
              <p className="mt-1 text-[11px]" style={{ color: "var(--text-muted)" }}>
                Suggested from the average of your last 3 months of expenses
                (x1.15). Feel free to overwrite.
              </p>
            )}
          </div>

          <p className="text-[11px] pt-1" style={{ color: "var(--text-muted)" }}>
            The dashboard Total Expenses ring shows current MTD vs this budget.
          </p>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-4 py-1.5 text-sm border"
            style={{
              borderColor: "var(--border-default)",
              color: "var(--text-secondary)",
              backgroundColor: "transparent",
            }}
            data-testid="budget-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || loading}
            className="rounded-md px-4 py-1.5 text-sm font-medium text-white disabled:opacity-60"
            style={{ backgroundColor: "#1F4FA3" }}
            data-testid="budget-save-btn"
          >
            {saving ? "Saving..." : "Save budget"}
          </button>
        </div>
      </div>
    </div>
  );
}
